import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Debug logging
console.log('🔥 Firebase module loading...');

// Your Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBxy25QH_5ei3A__CKs1_HLpJW_4rAl9ec",
  authDomain: "taxfile-f201b.firebaseapp.com",
  projectId: "taxfile-f201b",
  storageBucket: "taxfile-f201b.firebasestorage.app",
  messagingSenderId: "588650734726",
  appId: "1:588650734726:web:55d8d0f8f1f0d1f6f45d07",
  measurementId: "G-7KVWF1JVMZ"
};

// Track Firebase initialization status
export let firebaseInitialized = false;
export let firebaseError = null;

// VAPID key for web push (you'll need to generate this in Firebase Console)
// Go to: Firebase Console > Project Settings > Cloud Messaging > Web configuration > Web Push certificates
// IMPORTANT: Replace this with your actual VAPID key from Firebase Console
const VAPID_KEY = 'BDotglb0lQkG7q4iRX9K_uyWMpE9xPMCmNz0oL37LuI7mO0-fSsJTdY7slcLxTg9UK_uCM3HSF9iFcLHQHVucGg'; // Replace with actual VAPID key from Firebase Console

// Check if VAPID key is properly configured
export const isVapidKeyConfigured = () => {
  return VAPID_KEY && 
         VAPID_KEY !== 'YOUR_VAPID_KEY_HERE' && 
         VAPID_KEY.length > 50 && 
         VAPID_KEY.startsWith('B');
};

// Initialize Firebase
let app = null;
let db = null;
let auth = null;
let storage = null;
let messaging = null;

try {
  console.log('🔥 Initializing Firebase app...');
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase app initialized');
  
  // Initialize Firestore
  db = getFirestore(app);
  console.log('✅ Firestore initialized');
  
  // Initialize Auth
  auth = getAuth(app);
  console.log('✅ Auth initialized');
  
  // Initialize Storage
  storage = getStorage(app);
  console.log('✅ Storage initialized');
  
  // Initialize Cloud Messaging (FCM) for push notifications
  try {
    messaging = getMessaging(app);
    console.log('✅ Cloud Messaging initialized');
  } catch (msgError) {
    console.log('ℹ️ Cloud Messaging not available:', msgError.message);
  }
  
  firebaseInitialized = true;
  
  // Analytics initialization - optional, don't crash if fails
  try {
    const { getAnalytics } = require('firebase/analytics');
    getAnalytics(app);
    console.log('✅ Analytics initialized');
  } catch (analyticsError) {
    console.log('ℹ️ Analytics not available (this is OK):', analyticsError.message);
  }
} catch (error) {
  console.error('❌ Firebase initialization error:', error);
  firebaseError = error.message || 'Failed to connect to server';
  firebaseInitialized = false;
}

export { db, auth, storage, messaging };

// Push Notification Helper Functions
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return null;
    }
    
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      console.log('✅ Notification permission granted');
      return true;
    } else {
      console.log('❌ Notification permission denied');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

export const getFCMToken = async () => {
  try {
    if (!messaging) {
      console.log('Messaging not initialized');
      return { error: 'Push notifications are not available in this browser' };
    }

    // Check if VAPID key is configured
    if (!isVapidKeyConfigured()) {
      console.log('❌ VAPID key not configured. Please configure it in firebase.js');
      return { error: 'VAPID_KEY_NOT_CONFIGURED' };
    }

    // Register service worker first with update checking
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        updateViaCache: 'none'
      });
      console.log('✅ Service Worker registered:', registration);
      
      // Check for updates immediately and periodically
      registration.update();
      
      // Check for updates every 60 seconds when app is open
      setInterval(() => {
        registration.update();
        console.log('🔄 Checking for service worker updates...');
      }, 60000);
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('📦 New service worker found, installing...');
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('✅ New version available! Activating...');
            // Auto-activate the new service worker
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
      
    } catch (swError) {
      console.error('Service Worker registration failed:', swError);
      return { error: 'Could not register notification service. Please ensure you are using HTTPS.' };
    }

    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    
    if (currentToken) {
      console.log('✅ FCM Token obtained');
      return currentToken;
    } else {
      console.log('No registration token available');
      return { error: 'Could not get notification token. Please try again.' };
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    
    // Provide user-friendly error messages
    if (error.code === 'messaging/permission-blocked') {
      return { error: 'Notifications are blocked in your browser settings' };
    } else if (error.code === 'messaging/unsupported-browser') {
      return { error: 'Your browser does not support push notifications' };
    } else if (error.message?.includes('vapid') || error.message?.includes('VAPID')) {
      return { error: 'VAPID_KEY_NOT_CONFIGURED' };
    }
    
    return { error: error.message || 'Failed to enable notifications' };
  }
};

export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.log('Messaging not initialized');
    return () => {};
  }
  
  return onMessage(messaging, (payload) => {
    console.log('📬 Foreground message received:', payload);
    callback(payload);
  });
};

// Show local notification (for foreground messages)
export const showLocalNotification = (title, body, data = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    console.log('Notifications not available or not permitted');
    return;
  }
  
  // Use appointmentId-based tag to deduplicate with service worker notifications
  const tag = data?.appointmentId ? `appointment-${data.appointmentId}` : `taxfilepro-${Date.now()}`;
  
  const notification = new Notification(title, {
    body,
    icon: '/logo.png',
    badge: '/logo.png',
    tag: tag,
    requireInteraction: true,
    vibrate: [200, 100, 200],
    data
  });
  
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
  
  return notification;
};

export default app;
